/**
 * Moz Integration Module (Story 20.7)
 *
 * Barrel export + side-effect adapter registration.
 *
 * Registration happens here — NOT in `moz.ts` — to prevent the Failure 5a
 * anti-pattern documented in `google/index.ts:7`. Importing this module is
 * sufficient to register the adapter; importing `moz.ts` directly would give
 * you the adapter object without registration.
 */

import { registerAdapter } from "../registry";
import { mozAdapter } from "./moz";

// Side-effect registration (Story 20.7)
registerAdapter("moz", mozAdapter);

// Public surface — adapter + canonical constants
export {
  mozAdapter,
  MOZ_PLATFORM_VALUES,
  MOZ_METRICS,
  MOZ_DIMENSIONS,
  MOZ_SYNTHETIC_METRIC_KEYS,
  MOZ_FIXTURE_LAG_HOURS,
  MOZ_DEMO_SNAPSHOT_TARGET,
  MOZ_DEMO_SNAPSHOT_SCOPE,
  MOZ_DEMO_SNAPSHOT_ROW_COUNT,
  MOZ_API_VERSION,
  MOZ_JSON_RPC_BASE,
  MOZ_AUTONOMOUS_LIST_LIMIT,
  MOZ_LIST_LIMIT_MAX,
  MOZ_DAILY_TENANT_ROW_BUDGET,
  validateMozConnection,
  normalizeMozTarget,
  sanitizeMozErrorMessage,
  isMozScope,
  type MozPlatform,
  type MozScope,
} from "./moz";

// API helpers
export {
  fetchMozAnchorText,
  fetchMozBrandAuthority,
  fetchMozIndexMetadata,
  fetchMozLinkingRootDomains,
  fetchMozSiteMetrics,
  fetchMozTopPages,
  MOZ_ANCHOR_TEXT_SORTS,
  type FetchMozAnchorTextParams,
  type FetchMozBrandAuthorityParams,
  type FetchMozLinkingRootDomainsParams,
  type FetchMozSiteMetricsParams,
  type FetchMozTopPagesParams,
  type MozAnchorTextSort,
  type MozHelperResult,
  type MozIndexMetadataResult,
  type MozQuotaLookupResult,
  type MozValidationResult,
} from "./api";

// JSON-RPC client
export {
  mozJsonRpcCall,
  pollMozAsyncCall,
  generateMozRpcId,
  MOZ_METHOD_NAMES,
  MOZ_ASYNC_POLL_BACKOFF_MS,
  type MozMethodName,
  type MozJsonRpcCallOptions,
} from "./json-rpc";

// Errors
export {
  parseMozJsonRpcError,
  parseMozHttpError,
  mozErrorTypeToAdapterCode,
  MozApiCallError,
  MozAsyncTimeoutError,
  type MozApiError,
  type MozErrorType,
  type MozJsonRpcErrorEnvelope,
} from "./errors";

// Normalize
export { MozTargetValidationError, MOZ_SCOPE_VALUES } from "./normalize";

// Safe fetch
export {
  safeFetchMoz,
  resetMoz4xxTracker,
  record4xxForIntegration,
  getCooldownUntil,
  MOZ_COOLDOWN_4XX_THRESHOLD,
  MOZ_COOLDOWN_WINDOW_MS,
  MOZ_COOLDOWN_DURATION_MS,
} from "./safe-fetch";

// Cache policy
export {
  MOZ_METHOD_CACHE_TTL,
  getMozCacheTtl,
  buildMozMethodCacheKey,
  type MozCacheableMethod,
} from "./cache-policy";

// Row budget
export {
  buildMozTenantRowsKey,
  buildMozIntegrationRowsKey,
  buildMozTenantBreachMarkerKey,
  todayUtcYyyyMmDd,
  incrementMozRowsUsed,
  getMozTenantDailyRows,
  getMozIntegrationDailyRows,
  checkMozRowBudget,
  MOZ_ROWS_KV_TTL_SEC,
  type MozBudgetCheckResult,
} from "./row-budget";

// Transformers
export {
  transformMozAnchorTextRow,
  transformMozBrandAuthorityRow,
  transformMozDecimalField,
  transformMozIntegerField,
  transformMozLinkingRootDomainsRow,
  transformMozSiteMetricsRow,
  transformMozSpamScore,
  transformMozStringField,
  transformMozTopPagesRow,
  type MozAnchorTextApiRow,
  type MozBrandAuthorityApiRow,
  type MozLinkingRootDomainsApiRow,
  type MozReportType,
  type MozSiteMetricsApiRow,
  type MozTopPagesApiRow,
} from "./transformers";

// Snapshots
export {
  persistMozSnapshot,
  persistMozSnapshotFireAndForget,
  type PersistMozSnapshotInput,
  type MozD1Binding,
} from "./snapshots";
